import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFlask, FaMagic, FaLock, FaBolt, FaAtom, FaTelegramPlane } from 'react-icons/fa';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import FusionChamberAnimation from './FusionChamberAnimation';
import './CandyCombinerPage.css';

const FEATURE_CARDS = [
  {
    title: 'Drop In Candies',
    description: 'Feed the chamber with your rarest pulls and watch the lab spin up.',
    icon: <FaFlask />,
  },
  {
    title: 'Set Fusion Intent',
    description: 'Pick a vibe: Power, Style, or Rarity. The chamber adapts.',
    icon: <FaMagic />,
  },
  {
    title: 'Hatch an Ultra',
    description: 'Unseen candy variants with mythic stats and collector glow.',
    icon: <FaBolt />,
  },
];

const OUTCOME_CARDS = [
  { name: '???', label: 'Ultra Rare', note: 'Mutation locked' },
  { name: '???', label: 'Ultra Rare', note: 'Fusion locked' },
  { name: '???', label: 'Ultra Rare', note: 'Incubating' },
  { name: '???', label: 'Ultra Rare', note: 'Stabilizing' },
  { name: '???', label: 'Ultra Rare', note: 'Unknown' },
];

const TIMELINE_STEPS = [
  { title: 'Mix', copy: 'Load the chamber with 6 candies and set your intent.' },
  { title: 'Charge', copy: 'The coil powers up, blending luck with alchemy.' },
  { title: 'Pop', copy: 'A brand‑new ultra candy is born. Maybe.' },
];

const CandyCombinerPage = () => {
  const navigate = useNavigate();

  return (
    <div className="candy-combiner-page">
      <div className="combiner-swarm">
        {Array.from({ length: 14 }).map((_, index) => (
          <span key={index} className={`swimmer swimmer-${index + 1}`} />
        ))}
      </div>

      <section className="combiner-hero">
        <div className="hero-copy">
          <Badge className="soon-badge">CUMMING SOON</Badge>
          <h1>Candy Combiner</h1>
          <p>
            Fuse your candies into ultra‑rare, collector‑grade drops.
            The lab is warming up, but you can already peek inside.
          </p>
          <div className="hero-actions">
            <button
              className="hero-btn primary"
              onClick={() => navigate('/dckslap/candywrapper')}
            >
              Seal In Candy Wrapper
            </button>
            <button className="hero-btn ghost" disabled>
              See How It Works
            </button>
          </div>
          <div className="hero-note">
            <FaLock />
            <span>Combiner access unlocks soon. Keep stacking candy.</span>
          </div>
        </div>
        <div className="hero-chamber">
          <div className="chamber-shell">
            <FusionChamberAnimation />
            <div className="chamber-label">Fusion Chamber</div>
          </div>
        </div>
      </section>

      <section className="cumming-banner">
        <span className="banner-text">CUMMING SOON • CUMMING SOON • CUMMING SOON</span>
      </section>

      <section className="combiner-features">
        {FEATURE_CARDS.map((card) => (
          <Card key={card.title} className="feature-card">
            <CardHeader>
              <CardTitle>
                <span className="feature-icon">{card.icon}</span>
                {card.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>{card.description}</p>
              <Badge variant="outline">Coming Soon</Badge>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="combiner-demo">
        <div className="demo-column">
          <div className="demo-title">
            <FaAtom />
            <span>Your Ingredients</span>
          </div>
          <div className="slot-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="ingredient-slot">
                <span className="slot-label">Slot {index + 1}</span>
                <div className="slot-fill">
                  <div className="slot-incubator" />
                </div>
              </div>
            ))}
          </div>
          <div className="demo-caption">Drop 6 candies for max fusion potential.</div>
        </div>

        <div className="demo-center">
          <div className="fusion-core">
            <div className="fusion-glow" />
            <div className="fusion-meter">
              <span>Charge</span>
              <Progress value={42} className="fusion-progress" />
            </div>
            <div className="fusion-button">
              <FaBolt />
              <span>Fuse</span>
            </div>
          </div>
          <div className="center-note">Lab power currently capped at 42%.</div>
        </div>

        <div className="demo-column">
          <div className="demo-title">
            <FaMagic />
            <span>Target Output</span>
          </div>
          <div className="output-card">
            <div className="output-shroud" />
            <div className="output-content">
              <span className="output-title">Unknown Ultra</span>
              <span className="output-sub">Awaiting Stabilization</span>
            </div>
          </div>
          <div className="demo-caption">Ultra drops appear once the chamber unlocks.</div>
        </div>
      </section>

      <section className="combiner-outcomes">
        <div className="section-header">
          <h2>Ultra Outcomes</h2>
          <Badge variant="outline">Preview Only</Badge>
        </div>
        <div className="outcome-grid">
          {OUTCOME_CARDS.map((outcome, index) => (
            <Card key={`${outcome.label}-${index}`} className="outcome-card">
              <CardHeader>
                <CardTitle>{outcome.name}</CardTitle>
                <Badge className="outcome-badge">{outcome.label}</Badge>
              </CardHeader>
              <CardContent>
                <div className="outcome-mystery" />
                <div className="outcome-meta">{outcome.note}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="combiner-timeline">
        <div className="section-header">
          <h2>How It Will Work</h2>
          <Badge variant="outline">Soon™</Badge>
        </div>
        <div className="timeline-track">
          {TIMELINE_STEPS.map((step, index) => (
            <div key={step.title} className="timeline-step">
              <div className="step-index">{index + 1}</div>
              <div className="step-body">
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="combiner-footer">
        <div className="footer-card">
          <div className="footer-copy">
            <h3>Stay in the Loop</h3>
            <p>Hop into our Telegram — first to know when the lab goes live. 🧪</p>
          </div>
          <a
            href="https://t.me/RedDicks_XRD"
            target="_blank"
            rel="noopener noreferrer"
            className="hero-btn primary notify-link"
          >
            <FaTelegramPlane />
            Join Telegram
          </a>
        </div>
      </section>
    </div>
  );
};

export default CandyCombinerPage;
